import React, { useMemo, useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { vars } from '../../theme';
import { ScrollSync } from 'react-scroll-sync';
import { ColumnSortState, HBTableProps } from './HBTable.types';
import HBTableBody from './HBTableBody';
import HBTablePagination from './HBTablePagination';
import { TestIdBoundaryProvider } from '../common/providers/test-id-boundary.provider';
import { useSyncElementDimension } from '../../hooks/useSyncElementDimension';
import HBTableHeader from './HBTableHeader';
import HBTableFloatingScroller from './HBTableFloatingScroller';
import HBTableFloatingBulkActions from './HBTableFloatingBulkActions';
import HBTableFooter from './HBTableFooter';
import {
    ColumnDef,
    Table,
    useReactTable,
    getCoreRowModel,
    Row
} from '@tanstack/react-table';
import HBTableDndProvider from './HBTableDndProvider';

export function HBTable<T>(props: HBTableProps<T>) {
    const { sourceEl, setSourceEl, setTargetEl } = useSyncElementDimension<
        HTMLTableElement,
        HTMLDivElement
    >({ dimension: 'width' });

    const table = useTable(props);

    const content = (
        <>
            <HBTableHeader table={table} tableBodyEl={sourceEl} />
            <HBTableBody table={table} setTableBodyEl={setSourceEl} />
            {!props.hideFloatingBar && (
                <HBTableFloatingBulkActions
                    table={table}
                    showRowSelection={props.showRowSelection}
                />
            )}
            <HBTableFloatingScroller setScrollerEl={setTargetEl} />
            {props.footer && <HBTableFooter table={table} />}
            {!props.hidePagination && <HBTablePagination table={table} />}
        </>
    );

    return (
        <TestIdBoundaryProvider prefix={table.options.meta.testIdPrefix}>
            <ScrollSync>
                <Container
                    data-testid={`${table.options.meta.testIdPrefix}--container`}
                >
                    {props.isColumnDragAndDropEnabled ? (
                        <HBTableDndProvider
                            table={table}
                            onColumnOrderChange={props.onColumnOrderChange}
                        >
                            {content}
                        </HBTableDndProvider>
                    ) : (
                        content
                    )}
                </Container>
            </ScrollSync>
        </TestIdBoundaryProvider>
    );
}

export default HBTable;

const DEFAULT_SORTING: ColumnSortState = { id: '', desc: false };

function useTable<T>({
    data,
    columns,
    rowCount,
    pageCount,
    getRowId,
    onRowClick,
    isLoading,
    isInitialLoading,
    pagination,
    sorting = DEFAULT_SORTING,
    testIdPrefix,
    onPaginationChange,
    onSortingChange,
    isFirstColumnPinned,
    isLastColumnPinned,
    isLastColumnFloating,
    bulkActions,
    search,
    setSearch,
    filterElements,
    allFilterLabels,
    clearAllFilters,
    isColumnDragAndDropEnabled,
    emptyState,
    footer,
    selectedState,
    showRowSelection,
    itemLabel,
    itemsLabel
}: HBTableProps<T>) {
    const reactTableColumnDefinitions = useMemo(
        () =>
            columns.reduce(
                (definitions, column): Record<string, ColumnDef<T>> => {
                    const { id } = column;
                    definitions[id] = {
                        id: id,
                        accessorKey: id,
                        meta: { original: column }
                    };
                    return definitions;
                },
                {}
            ),
        [columns]
    );

    const [columnOrder, setColumnOrder] = useState<string[]>(() =>
        columns.map(column => column.id)
    );

    useEffect(() => {
        setColumnOrder(columns.map(column => column.id));
    }, [columns]);

    const reactTableColumns = useMemo(
        () =>
            columnOrder
                .map(id => reactTableColumnDefinitions[id])
                .filter(Boolean),
        [reactTableColumnDefinitions, columnOrder]
    );

    const { current: helper } = useRef(new TableHelper(() => table));

    const table: Table<T> = useReactTable({
        data: data,
        columns: reactTableColumns,
        rowCount,
        pageCount,
        getRowId,
        getCoreRowModel: getCoreRowModel(),
        onPaginationChange,
        onSortingChange: updater => {
            onSortingChange(prev =>
                typeof updater === 'function'
                    ? updater([prev ?? DEFAULT_SORTING])[0]
                    : updater[0]
            );
        },
        manualPagination: true,
        manualSorting: true,
        state: {
            pagination,
            sorting: [sorting],
            columnOrder,
            ...(selectedState
                ? {
                      rowSelection: selectedState.selected.reduce((acc, id) => {
                          acc[id] = true;
                          return acc;
                      }, {} as Record<string, boolean>)
                  }
                : {})
        },
        ...(selectedState
            ? {
                  onRowSelectionChange: updater => {
                      const currentSelection = selectedState.selected.reduce(
                          (acc, id) => {
                              acc[id] = true;
                              return acc;
                          },
                          {} as Record<string, boolean>
                      );

                      const newSelection =
                          typeof updater === 'function'
                              ? updater(currentSelection)
                              : updater;

                      const newSelectedIds = Object.keys(newSelection).filter(
                          id => newSelection[id]
                      );
                      selectedState.setSelected(newSelectedIds);
                  }
              }
            : {}),
        onColumnOrderChange: setColumnOrder,
        meta: {
            isLoading,
            isInitialLoading,
            testIdPrefix,
            onRowClick,
            isFirstColumnPinned,
            isLastColumnPinned,
            isLastColumnFloating,
            bulkActions,
            search,
            setSearch,
            helper,
            filterElements,
            clearAllFilters,
            allFilterLabels,
            isColumnDragAndDropEnabled,
            emptyState,
            footer,
            showRowSelection,
            itemLabel,
            itemsLabel
        }
    });

    return table;
}

export type HBTableHelperType = TableHelper;

class TableHelper {
    private readonly getTable: () => Table<any>;

    constructor(getTable: () => Table<any>) {
        this.getTable = getTable;
    }

    private get table(): Table<any> {
        return this.getTable();
    }

    private get isAllSelectionMode(): boolean {
        return this.rowSelection.all;
    }

    private get currPageRows(): Row<any>[] {
        return this.table.getRowModel().rows;
    }

    private get rowSelection(): Record<string, boolean> {
        return this.table.getState().rowSelection;
    }

    get hasBulkActions(): boolean {
        const { bulkActions } = this.table.options.meta;
        return bulkActions?.length > 0;
    }

    get hasRowSelection(): boolean {
        const { showRowSelection } = this.table.options.meta;
        return showRowSelection || this.hasBulkActions;
    }

    get isRowClickable(): boolean {
        const { onRowClick } = this.table.options.meta;
        return !!onRowClick || this.hasRowSelection;
    }

    get shouldUseWhiteShadow(): boolean {
        return !this.isRowClickable && !this.hasBulkActions;
    }

    get selectedRowCount(): number {
        const rowSelectionCopy = { ...this.rowSelection };
        delete rowSelectionCopy.all;
        const rowIds = Object.keys(rowSelectionCopy);

        return this.isAllSelectionMode
            ? this.table.getRowCount() - rowIds.length
            : rowIds.length;
    }

    get isSomeRowsSelected(): boolean {
        return this.isAllSelectionMode
            ? this.selectedRowCount < this.table.getRowCount()
            : this.selectedRowCount > 0;
    }

    get isAllRowsSelected(): boolean {
        return this.selectedRowCount === this.table.getRowCount();
    }

    get isRowsSelected(): boolean {
        return this.isSomeRowsSelected || this.isAllRowsSelected;
    }

    get isSomeCurrPageRowsSelected(): boolean {
        return this.currPageRows.some(row => this.isRowSelected(row));
    }

    get isAllCurrPageRowsSelected(): boolean {
        return this.currPageRows.every(row => this.isRowSelected(row));
    }

    private getSelectionPolarity(value: boolean): boolean {
        return this.isAllSelectionMode ? !value : value;
    }

    isRowSelected(row: Row<any>): boolean {
        return this.getSelectionPolarity(row.id in this.rowSelection);
    }

    toggleTableRowSelection(row: Row<any>, newSelected?: boolean) {
        const newState = newSelected ?? !this.isRowSelected(row);
        row.toggleSelected(this.getSelectionPolarity(newState));
    }

    toggleAllCurrPageRowsSelected(checked: boolean) {
        this.table.toggleAllRowsSelected(this.getSelectionPolarity(checked));
    }
}

// -------------------------- Styles -------------------------- //

const Container = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${vars.colors.surface.container};
    border: 1px solid ${vars.colors.stroke.secondary};
    border-radius: ${vars.radius.sm};
`;
